// 브라우저가 서버와 말하는 법 — 한 자리
//
// 토큰을 `localStorage` 에 둔다. 쿠키를 안 쓰는 이유가 둘이다. 도메인 없는 IP 직결
// HTTP 라 쿠키의 안전 속성 대부분이 성립하지 않고, 같은 주소에 두 역할이 동시에 붙는
// 사용법이 실제로 있다 — CT 가 자기 폰으로 viewer 화면을 함께 여는 것이 그것이다.
// 쿠키면 브라우저가 그 둘을 하나로 묶어 버린다.
//
// **세션 승계가 여기 걸려 있다.** 새로고침해도 토큰이 남아 있으므로 브라우저는 다시
// 입장하지 않고 같은 세션으로 돌아간다. 그래서 새로고침이 접속 수를 늘리지 않는다.

import { routes } from '../../contract/routes.ts'
import {
  CLOSE_UNKNOWN_SESSION,
  sessionGoneCause,
  type SessionGoneCause,
} from '../../contract/ws.ts'
import { DEFAULT_LANG, errors, type Lang } from '../../i18n/index.ts'

export const VIEWER_TOKEN_KEY = 'rally-ops-room.session'
export const CT_TOKEN_KEY = 'rally-ops-room.ct'

/**
 * 토큰과 **함께** 남기는 자기 게임 ID.
 *
 * 토큰만 남기면 새로고침으로 돌아온 화면이 자기가 누구로 들어왔는지 모른다 — 서버는
 * 알지만 화면이 물을 근거가 없다. 그러면 「입장했습니다」 아래 이름 자리가 빈 채로
 * 남고, 사람은 자기가 제대로 들어온 것인지 확인할 방법을 잃는다.
 */
export const VIEWER_GAME_ID_KEY = 'rally-ops-room.game-id'

export function readToken(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // 사생활 보호 모드에서 접근이 막히는 브라우저가 있다. 그때는 토큰이 없는 것으로
    // 보고 다시 입장하게 둔다 — 화면이 하얗게 죽는 것보다 낫다.
    return null
  }
}

export function writeToken(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // 못 남겨도 이번 세션은 돈다. 새로고침하면 다시 입장해야 할 뿐이다.
  }
}

export function clearToken(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // 위와 같다.
  }
}

/**
 * 세션을 잃고 입장 화면으로 물러난 탭에 남기는 표지. 값은 원인이다. 입장 화면이 이것을 보고 왜
 * 돌아왔는지와 다음에 무엇을 하면 되는지를 말한다.
 *
 * **`sessionStorage` 인 이유는 한 번, 한 탭만 말해야 하기 때문이다.** 주소에 붙이면 새로고침마다
 * 같은 문장이 다시 서고, `localStorage` 면 같은 브라우저의 다른 탭까지 그 문장을 본다.
 */
const SESSION_GONE_NOTICE_KEY = 'rally-ops-room.session-gone'

/**
 * 열람 화면이 세션을 잃었을 때 부른다. 저장한 토큰을 지우고 표지를 남긴다(T4 개정 둘째).
 *
 * 토큰을 남긴 채 입장 화면으로 가면 그 화면이 토큰을 보고 「입장했습니다」를 먼저 그린 뒤 서버의
 * 답을 받고서야 물러난다. 지워진 사람에게 한 순간이라도 「입장했습니다」를 보이는 것은 거짓이다.
 *
 * **저장된 토큰이 죽은 토큰과 같을 때만 지운다.** 같은 브라우저의 다른 탭이 그 사이 다시 입장해
 * 새 토큰을 써 두었을 수 있는데, 얼어 있던 이 탭이 깨어나 옛 토큰으로 거절을 받고 새 토큰까지
 * 지우면 그 탭은 새로고침 한 번에 입장을 잃는다(적대 리뷰가 찾았다).
 */
export function markSessionGone(deadToken: string, cause: SessionGoneCause): void {
  if (readToken(VIEWER_TOKEN_KEY) === deadToken) {
    clearToken(VIEWER_TOKEN_KEY)
    clearToken(VIEWER_GAME_ID_KEY)
  }
  try {
    window.sessionStorage.setItem(SESSION_GONE_NOTICE_KEY, cause)
  } catch {
    // 못 남기면 입장 화면이 이유를 말하지 못할 뿐이고, 코드 입력으로는 돌아간다.
  }
}

/**
 * 구성 화면이 CT 세션을 잃었을 때 부른다(배포 전 보수 회차, 2026-10-07). **저장된 토큰이 이 탭의 죽은
 * 토큰과 같을 때만 지운다.** 같은 브라우저에서 CT 로 두 번 들어오면 탭마다 토큰이 다른데 저장 칸은 하나다.
 * 관리 코드를 재발급하면 누른 탭만 남고 다른 탭은 끊기는데, 끊긴 탭이 칸을 무조건 지우면 남아야 할 탭의
 * 토큰까지 지운다(적대 검토가 찾았다). 열람 쪽 `markSessionGone` 과 같은 가드다.
 */
/** 구성 화면이 CT 세션을 잃은 이유. 서버가 말한 원인에 「다른 탭이 이어 쓴다」 하나를 더한다. */
export type CtGoneCause = SessionGoneCause | 'other-tab'

const CT_GONE_NOTICE_KEY = 'rally-ops-room.ct-gone'

/**
 * 끊긴 CT 화면이 남기는 표지(2026-10-07). 새로고침해도 왜 돌아왔는지가 남게 한다. 지우면 일반 문장만 남아
 * 무엇을 할지 말하지 않았다(대리 관측 둘째 판). 열람 쪽 표지와 같은 이유로 `sessionStorage` 다.
 */
export function markCtGone(cause: CtGoneCause): void {
  try {
    window.sessionStorage.setItem(CT_GONE_NOTICE_KEY, cause)
  } catch {
    // 못 남기면 새로고침 뒤 이유를 말하지 못할 뿐이다.
  }
}

export function readCtGoneNotice(): CtGoneCause | null {
  try {
    const raw = window.sessionStorage.getItem(CT_GONE_NOTICE_KEY)
    if (raw === null) return null
    return raw === 'removed' || raw === 'kicked' || raw === 'rotated' || raw === 'other-tab' ? raw : 'unknown'
  } catch {
    return null
  }
}

/** CT 로 다시 들어오면 지운다. */
export function clearCtGoneNotice(): void {
  try {
    window.sessionStorage.removeItem(CT_GONE_NOTICE_KEY)
  } catch {
    // 위와 같다.
  }
}

/**
 * 같은 브라우저의 다른 탭이 CT 로 다시 들어와 저장 칸에 써 둔 토큰. 없으면 null 이다.
 *
 * **끊긴 탭은 이 토큰을 이어받지 않는다.** 이어받으면 두 탭이 한 토큰을 쓰는데, 서버의 연결 표는 토큰 하나에
 * 소켓 하나라 두 탭이 서로의 소켓을 밀어낸다. 2026-10-07 대리 관측 둘째 판에서는 이어받은 직후 앞 토큰의 끊김
 * 표시가 새 토큰에 다시 걸려 저장 칸까지 지웠고, 재발급을 누른 탭이 끊겼다. 그래서 끊긴 탭은 「다른 탭에서
 * 계속한다」고만 말한다.
 */
export function otherTabCtToken(deadToken: string): string | null {
  const stored = readToken(CT_TOKEN_KEY)
  return stored !== null && stored !== deadToken ? stored : null
}

export function forgetCtToken(deadToken: string): void {
  if (readToken(CT_TOKEN_KEY) === deadToken) clearToken(CT_TOKEN_KEY)
}

/**
 * 표지의 원인. 없으면 null. **읽기만 하고 지우지 않는다.** 지우는 일은 `clearSessionGoneNotice` 가
 * 따로 한다.
 *
 * 둘로 가른 이유는 React 가 개발 모드에서 상태 초기값 함수를 두 번 부르기 때문이다. 읽으면서
 * 지우면 둘째 부름이 「없다」를 보고, 그 값이 화면에 남는다.
 */
export function readSessionGoneNotice(): SessionGoneCause | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_GONE_NOTICE_KEY)
    if (raw === null) return null
    return raw === 'removed' || raw === 'kicked' || raw === 'rotated' ? raw : 'unknown'
  } catch {
    return null
  }
}

export function clearSessionGoneNotice(): void {
  try {
    window.sessionStorage.removeItem(SESSION_GONE_NOTICE_KEY)
  } catch {
    // 위와 같다.
  }
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; message: string; code?: string | undefined }

/**
 * 지금 고른 언어 하나. **접속 층이 React 밖이라 상태를 볼 수 없어서 여기 둔다.**
 *
 * 모듈 변수를 쓰는 것이 지름길처럼 보이지만 아니다 — 한 사람이 한 브라우저에서 두 언어를
 * 동시에 쓰는 상태가 없으므로 **값이 하나뿐인 것이 사실이고**, 화면마다 따로 들면 그쪽이
 * 오히려 없는 구분을 만든다. 쓰는 곳은 `useLang` 하나이고 읽는 곳은 아래 한 자리다.
 */
let activeLang: Lang = DEFAULT_LANG

/** `useLang` 이 고른 값을 알려 준다. 다른 데서 부르지 않는다. */
export function setActiveLang(lang: Lang): void {
  activeLang = lang
}

/** 문장 안의 `{value}` 를 채운다. 화면들이 쓰는 `fill` 과 같은 규칙이다. */
function fill(template: string, value: string | undefined): string {
  return value === undefined ? template : template.replaceAll('{value}', value)
}

/**
 * 서버가 보낸 거절을 **지금 언어의 한 문장으로** 바꾼다.
 *
 * 규칙이 셋이고 순서가 곧 신뢰 순서다.
 *
 * 1. **코드를 알면 내 말로 만든다.** 번역은 화면의 몫이고 서버는 무엇이 잘못됐는지만 말한다.
 * 2. **모르면 서버가 실은 문장을 그대로 쓴다.** 코드가 아직 안 붙은 자리(계산으로 만들어지는
 *    문장들)가 남아 있고, 그때 빈 문장을 내는 것이 이 제품에서 가장 나쁜 답이다.
 * 3. **둘 다 없으면 상태 코드라도 말한다.** 아무 말도 안 하는 것보다 낫다.
 */
function sayFailure(
  payload: unknown,
  status: number,
): { message: string; code?: string | undefined } {
  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as {
    message?: unknown
    code?: unknown
    value?: unknown
  }
  const code = typeof body.code === 'string' ? body.code : undefined
  const value = typeof body.value === 'string' ? body.value : undefined
  const served = typeof body.message === 'string' ? body.message : undefined

  const table = errors(activeLang) as Record<string, string | undefined>
  const known = code === undefined ? undefined : table[code]
  if (known !== undefined && known !== '') return { message: fill(known, value), code }
  if (served !== undefined) return { message: served, code }
  return { message: fill(table.requestFailed ?? '', String(status)), code }
}

interface CallOptions {
  body?: unknown
  token?: string | null
}

/**
 * 서버를 부른다. **실패를 예외로 던지지 않는다.**
 *
 * 화면이 보여 줘야 하는 것은 "무엇이 잘못됐는지" 한 문장이고, 그 문장은 서버가 이미
 * 골라 보내 준다. 예외로 던지면 화면마다 try 를 두르게 되고 결국 "무언가 잘못됐습니다"
 * 로 수렴한다 — 사용자에게 아무것도 알려 주지 않는 그 문장이 이 제품에서 가장 나쁘다.
 */
export async function call<T>(
  route: { method: string; path: string },
  options: CallOptions = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (options.token !== undefined && options.token !== null) {
    headers.authorization = `Bearer ${options.token}`
  }

  let response: Response
  try {
    response = await fetch(route.path, {
      method: route.method,
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
    })
  } catch {
    // 상태 0 은 「응답 자체가 없었다」는 뜻이라 서버가 실어 줄 문장도 없다. 이 한 문장만은
    // 접속 층이 자기 표에서 꺼낸다.
    return { ok: false, status: 0, message: errors(activeLang).networkUnreachable }
  }

  let payload: unknown = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }

  if (!response.ok) {
    const said = sayFailure(payload, response.status)
    return { ok: false, status: response.status, message: said.message, code: said.code }
  }

  return { ok: true, data: payload as T }
}

/** WebSocket 주소. 지금 페이지의 호스트에 그대로 붙는다 — 주소가 하나라는 것이 전제다. */
export function websocketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/api/ws`
}

export { routes }

// ── 살아 있는 상태 ─────────────────────────────────────────────────────────
//
// 접속 명단은 요청해서 받는 것이 아니라 **밀려오는 것**이다. CT 가 새로고침을 눌러야
// 명단이 갱신되면, 그 명단은 "지금 누가 들어와 있는가" 가 아니라 "아까 누가 있었는가"
// 가 된다. 그리고 CT 는 그 차이를 화면에서 구별할 수 없다.
//
// **서버가 보내는 ping 에 답하는 것은 브라우저의 네트워크 스택이다.** 이 훅은 그 답에
// 관여하지 않는다 — 페이지 자바스크립트가 배경에서 늦춰져도 연결은 살아 있어야 하고,
// 그것이 앱을 잠깐 전환한 사람이 명단에서 사라지지 않는 이유다.

import { useEffect, useState } from 'react'

import type { PresenceEntry, RoomPublic } from '../../contract/schema.ts'

export { CLOSE_UNKNOWN_SESSION }

export type CloseVerdict = 'retry' | 'session-gone'

/**
 * 닫힘 코드를 읽어 **다시 붙을 것인가**를 정한다.
 *
 * 순단과 죽은 세션을 가르는 것이 전부다. 섞으면 둘 다 틀린다 — 전부 다시 붙으면 죽은
 * 토큰이 2초마다 영원히 서버를 두드리고(재배포 뒤 20~50명이 동시에), 전부 안 붙으면
 * 잠깐 끊긴 정상 접속자가 명단에서 사라진다.
 *
 * 4000번대 전체를 죽은 세션으로 읽지 않는 이유: 그 대역은 응용이 쓰라고 비워 둔 자리라
 * 앞으로 다른 뜻이 더 붙는다. 대역으로 읽으면 그때 붙는 코드들이 전부 사람을 쫓아낸다.
 */
export function classifySocketClose(code: number): CloseVerdict {
  return code === CLOSE_UNKNOWN_SESSION ? 'session-gone' : 'retry'
}

export interface LiveState {
  entries: PresenceEntry[]
  room: RoomPublic | null
  connected: boolean
  /**
   * 서버가 이 토큰을 모른다고 답했다. **화면은 이 값을 보고 입장 상태를 거둬야 한다** —
   * 서버에 세션이 없는데 「입장했습니다」가 떠 있으면 화면이 사실이 아닌 것을 단언한다.
   */
  sessionGone: boolean
  /** 세션을 거둔 원인. 거두지 않았으면 null. 화면은 이것으로 돌아온 이유의 문장을 고른다. */
  goneCause: SessionGoneCause | null
}

// ── 소켓은 한 페이지에 하나다 ──────────────────────────────────────────────
//
// **서버의 연결 표는 세션 토큰 하나당 소켓 하나만 든다.** 새 소켓이 붙으면 앞 소켓을
// `terminate` 한다 — 새로고침으로 돌아온 사람이 접속 수를 늘리지 않게 하는 승계 처리다.
//
// 그래서 같은 토큰으로 소켓을 둘 열면 **둘이 서로를 쫓아내고 2초 뒤 다시 붙는 깜빡임**이
// 생긴다. 화면마다 자기 구독을 갖고 싶어지는 것은 자연스러운 욕구인데(작전 구성 화면이
// 시퀀스 방송을 받으려다 실제로 그렇게 했다), 그 욕구를 각자 풀게 두면 화면이 늘 때마다
// 이 고리가 다시 생긴다. 그래서 여기서 하나로 모은다 — **구독은 여럿, 소켓은 하나.**

export interface ServerEvent {
  kind?: string
  [key: string]: unknown
}

export interface ChannelStatus {
  connected: boolean
  sessionGone: boolean
  goneCause: SessionGoneCause | null
}

export interface ChannelListener {
  onEvent: (message: ServerEvent) => void
  onStatus: (status: ChannelStatus) => void
}

interface Channel {
  socket: WebSocket | null
  listeners: Set<ChannelListener>
  status: ChannelStatus
  retry: ReturnType<typeof setTimeout> | null
  idle: ReturnType<typeof setTimeout> | null
  alive: boolean
}

const channels = new Map<string, Channel>()

function announce(channel: Channel, status: ChannelStatus): void {
  channel.status = status
  for (const listener of [...channel.listeners]) listener.onStatus(status)
}

function openChannel(token: string, channel: Channel): void {
  if (!channel.alive) return
  const socket = new WebSocket(websocketUrl())
  channel.socket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({ kind: 'subscribe', sessionToken: token }))
    announce(channel, { connected: true, sessionGone: false, goneCause: null })
  })

  socket.addEventListener('message', (event: MessageEvent<string>) => {
    let parsed: unknown
    try {
      parsed = JSON.parse(event.data)
    } catch {
      return
    }
    if (typeof parsed !== 'object' || parsed === null) return
    for (const listener of [...channel.listeners]) listener.onEvent(parsed as ServerEvent)
  })

  socket.addEventListener('close', (event: CloseEvent) => {
    // 밀려난 옛 소켓의 close 가 새 소켓이 붙은 뒤에 도착하는 일이 있다. 그때 상태를
    // 갈아치우면 방금 붙은 연결이 끊긴 것으로 보인다.
    if (channel.socket !== socket) return
    channel.socket = null

    // **서버가 이 세션을 모른다고 답했으면 다시 붙지 않는다.** 붙어 봐야 매번 같은
    // 코드로 끊기고, 그동안 화면은 「입장했습니다」를 띄운 채 2초마다 서버를 두드린다.
    // 재배포 한 번에 연맹원 전원이 그 상태가 된다.
    if (classifySocketClose(event.code) === 'session-gone') {
      announce(channel, {
        connected: false,
        sessionGone: true,
        goneCause: sessionGoneCause(event.reason),
      })
      return
    }
    announce(channel, { connected: false, sessionGone: false, goneCause: null })
    // 그 밖의 닫힘은 순단으로 본다. **같은 토큰으로 붙으므로 새 접속이 아니라 승계다.**
    if (channel.alive) {
      channel.retry = setTimeout(() => {
        openChannel(token, channel)
      }, 2_000)
    }
  })
}

/**
 * 서버가 밀어 주는 것을 구독한다. **같은 토큰의 구독자들이 소켓 하나를 함께 쓴다.**
 *
 * 돌려받는 함수를 부르면 구독을 뗀다. 마지막 구독자가 떨어져도 소켓을 곧바로 닫지
 * 않는다 — 개발 모드의 이중 마운트와 탭 전환이 구독 수를 잠깐 0으로 만드는데, 그때마다
 * 닫았다 열면 서버가 접속 명단을 두 번 다시 방송하고 그 잡음이 전투 중 CT 화면에서
 * 깜빡임으로 보인다.
 */
export function subscribeServerEvents(token: string, listener: ChannelListener): () => void {
  let found = channels.get(token)
  if (found === undefined) {
    found = {
      socket: null,
      listeners: new Set(),
      status: { connected: false, sessionGone: false, goneCause: null },
      retry: null,
      idle: null,
      alive: true,
    }
    channels.set(token, found)
    openChannel(token, found)
  }
  const channel = found
  if (channel.idle !== null) {
    clearTimeout(channel.idle)
    channel.idle = null
  }
  channel.listeners.add(listener)
  listener.onStatus(channel.status)

  return () => {
    channel.listeners.delete(listener)
    if (channel.listeners.size > 0) return
    channel.idle = setTimeout(() => {
      if (channel.listeners.size > 0) return
      channel.alive = false
      if (channel.retry !== null) clearTimeout(channel.retry)
      channel.socket?.close()
      channels.delete(token)
    }, 1_000)
  }
}

export function useLiveState(token: string | null): LiveState {
  const [state, setState] = useState<LiveState>({
    entries: [],
    room: null,
    connected: false,
    sessionGone: false,
    goneCause: null,
  })

  useEffect(() => {
    if (token === null || token === '') return
    return subscribeServerEvents(token, {
      onEvent: (message) => {
        if (message.kind === 'presence.updated' && message['entries'] !== undefined) {
          setState((previous) => ({ ...previous, entries: message['entries'] as PresenceEntry[] }))
        }
        if (message.kind === 'room.state' && message['room'] !== undefined) {
          setState((previous) => ({ ...previous, room: message['room'] as RoomPublic }))
        }
      },
      onStatus: (status) => {
        setState((previous) => ({ ...previous, ...status }))
      },
    })
  }, [token])

  return state
}
